import * as React from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { Box, Paper, Switch, Typography } from '@mui/material'
import HeaderTheChart from './HeaderTheYearComparisonChart'
import CustomTooltip from './CustomTooltip'
import ChartLoading from './ChartLoading'
import { useAppTheme } from '@/theme/ThemeContext'
import { isCanonicalData, isDateString } from '@/utils/chartUtils'

type SeriesValue = number | null | undefined
type Point = { x: number | string; label?: string; curr?: number; prev?: number; second?: number; third?: number }
type FooterItem = { label: string; shortLabel?: string; color: string; mode?: number[] }

type Props = {
  year?: number
  currentYearData?: Array<number | [number, number]>
  previousYearData?: Array<number | [number, number]>
  defaultCompare?: boolean
  rtl?: boolean
  height?: number
  legend?: boolean
  legend2?: boolean
  headerLayout?: 'default' | 'bar'
  showCompareLabel?: boolean
  headerTitle?: string
  showFooter?: boolean
  footerItems?: FooterItem[]
  seriesLabels?: {
    curr: string
    prev?: string
    second?: string
    third?: string
  }
  xAxisLabel?: string
  yAxisLabel?: string
  showMarkers?: boolean
  loading?: boolean
  periodLabels?: string[]
}


function buildData(
  current: Array<number | [number, number]>,
  previous?: Array<number | [number, number]>,
  second?: Array<number | [number, number]>,
  third?: Array<number | [number, number]>,
  modeId?: number
): Point[] {
  const normalize = (arr: Array<number | [number, number]>) =>
    arr.map((v, i) => (Array.isArray(v) ? v : [i * 20, v]))

  const c = normalize(current)
  const p = previous ? normalize(previous) : []
  const s = second ? normalize(second) : []
  const t = third ? normalize(third) : []

  if (modeId !== 1 && modeId !== 2) {
    const keys = new Set<number>([
      ...c.map(t => t[0]),
      ...p.map(t => t[0]),
      ...s.map(t => t[0]),
      ...t.map(t => t[0]),
    ])
    return [...keys]
      .sort((a, b) => a - b)
      .map(x => ({
        x,
        curr: c.find(t => t[0] === x)?.[1],
        prev: p.find(t => t[0] === x)?.[1],
        second: s.find(t => t[0] === x)?.[1],
        third: t.find(t => t[0] === x)?.[1],
      }))
  } else {
    const keys = new Set<number>([...c.map(t => t[0]), ...s.map(t => t[0])])
    return [...keys]
      .sort((a, b) => a - b)
      .map(x => ({
        x,
        curr: c.find(t => t[0] === x)?.[1],
        second: s.find(t => t[0] === x)?.[1],
      }))
  }
}

export default function YearComparisonChart({
  data: dashboardStats,
  comparedData,
  year = new Date().getFullYear(),
  defaultCompare = true,
  rtl = true,
  height = 280,
  legend = true,
  legend2 = true,
  headerLayout = 'default',
  headerTitle = 'נתוני צריכת מים',
  footerItems,
  seriesLabels,
  periodicData,
  loading,
  periodLabels,
}: {
  data?: any[]
  periodicData?: boolean
  comparedData?: {
    previousData: SeriesValue[]
    currentData: SeriesValue[]
  }
} & Props) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const chartContainerRef = React.useRef<HTMLDivElement | null>(null)
  const [chartWidth, setChartWidth] = React.useState(700)

  const [compare, setCompare] = React.useState(defaultCompare)
  const [modeId, setModeId] = React.useState<number>(1)
  const prevYear = year - 1
  const hasPreviousYear = Boolean(
    comparedData?.previousData?.length &&
      comparedData.previousData.some(
        value => value != null && Number.isFinite(Number(value))
      )
  )

  const isCanonical = isCanonicalData(dashboardStats || [])

  const data = React.useMemo(
    () => {
      if (isCanonical && dashboardStats) {
        if (modeId === 1) {
          // Day only: dailySupply → curr, dailyConsumption → second
          return dashboardStats.map((p: any) => ({ x: p.x, curr: p.curr, second: p.second }))
        }
        if (modeId === 2) {
          // Night only: nightlySupply → curr, nightlyConsumption → second
          return dashboardStats.map((p: any) => ({ x: p.x, curr: p.prev, second: p.third }))
        }
        // Mode 3: all 4 series
        return dashboardStats
      }
      if (periodicData) {
        const current = comparedData?.currentData ?? []
        const previous = comparedData?.previousData ?? []
        const pointCount = Math.max(current.length, previous.length)

        const normalizeValue = (value: SeriesValue) =>
          value != null && Number.isFinite(Number(value))
            ? Number(value)
            : undefined

    return Array.from({ length: pointCount }, (_, index) => ({
      // Recharts uses the X value as the categorical identity. Period labels can
      // repeat across years, so keep the identity unique and format it separately.
      x: `period-${index}`,
      label: periodLabels?.[index] ?? String(index + 1),
      curr: normalizeValue(current[index]),
      prev: normalizeValue(previous[index]),
    }))
      }

      const getStatValue = (stat: any, field: string) => {
        const key = Object.keys(stat || {}).find(k => k !== 'id')
        return key ? stat[key]?.[field] : undefined
      }

      const supplyData = modeId === 1 || modeId === 3
        ? dashboardStats?.map(stat => getStatValue(stat, 'supply'))
        : dashboardStats?.map(stat => getStatValue(stat, 'nightSupply'))

      const nightSupplyData = compare
        ? dashboardStats?.map(stat => getStatValue(stat, 'nightSupply'))
        : undefined

      const consumptionData = modeId === 1 || modeId === 3
        ? dashboardStats?.map(stat => getStatValue(stat, 'consumption'))
        : dashboardStats?.map(stat => getStatValue(stat, 'nightConsumption'))

      const nightConsumptionData = dashboardStats?.map(
        stat => getStatValue(stat, 'nightConsumption')
      )

      return buildData(
        supplyData || [],
        nightSupplyData,
        consumptionData || [],
        nightConsumptionData,
        modeId
      )
    },
    [dashboardStats, compare, modeId, periodicData, comparedData, isCanonical, periodLabels]
  )

  React.useEffect(() => {
    const container = chartContainerRef.current
    if (!container) return

    const updateWidth = () => setChartWidth(container.getBoundingClientRect().width)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const xAxisInterval = React.useMemo(() => {
    if (!periodicData) return isCanonical ? 4 : undefined

    const containsOnlyYears =
      periodLabels?.length === data.length &&
      periodLabels.every(label => /^\d{4}$/.test(label))
    if (containsOnlyYears) return 0

    const maxVisibleLabels = Math.max(3, Math.floor(chartWidth / 80))
    return Math.max(0, Math.ceil(data.length / maxVisibleLabels) - 1)
  }, [chartWidth, data.length, isCanonical, periodicData, periodLabels])



  const colorDaySupply = '#72A4EB'
  const colorDayCons = '#AEEA8C'
  const colorNightSupply = '#904199'
  const colorNightCons = '#F9A6F9'

  const blue = modeId === 2 ? colorNightSupply : colorDaySupply
  const secondColor = modeId === 2 ? colorNightCons : colorDayCons
  const thirdColor = colorNightSupply
  const fourthColor = colorNightCons
  const axisFg = isDarkMode ? '#94A3B8' : '#93A3B5'
  const grid = isDarkMode ? '#374151' : '#E6ECF2'
  const cardBorder = isDarkMode ? '#374151' : '#E6ECF2'
  const cardBg = isDarkMode ? '#202B40' : '#FAFBFD'
  const textPrimary = isDarkMode ? '#FFFFFF' : '#003F61'

  const footer = footerItems ?? [
    { label: 'נצרך לילה', shortLabel: 'נ.לילה', color: colorNightCons, mode: [2, 3] },
    { label: 'סופק לילה', shortLabel: 'ס.לילה', color: colorNightSupply, mode: [2, 3] },
    { label: 'נצרך יום', shortLabel: 'נ.יום', color: colorDayCons, mode: [1, 3] },
    { label: 'סופק יום', shortLabel: 'ס.יום', color: colorDaySupply, mode: [1, 3] },
  ]
  const activeFooterItems = footer.filter(
    item => !item.mode || item.mode.includes(modeId)
  )

  const currName = seriesLabels?.curr ?? `${year}`
  const prevName = seriesLabels?.prev ?? `${prevYear}`
  const secondName = seriesLabels?.second ?? 'צ.יום לילה'
  const thirdName = seriesLabels?.third ?? 'צ.יום'

  React.useEffect(() => {
    setCompare(defaultCompare && (!periodicData || hasPreviousYear))
  }, [defaultCompare, periodicData, hasPreviousYear])

  if (loading) return <ChartLoading height={height} />

  const showBarHeader = headerLayout === 'bar' && legend2

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2.5,
        border: `1px solid ${cardBorder}`,
        bgcolor: cardBg,
        width: '100%',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      {periodicData && (
        <Box
          dir="rtl"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pb: 1.5,
            mb: 1.5,
            borderBottom: `1px solid ${grid}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Switch
              size="small"
              checked={compare}
              disabled={!hasPreviousYear}
              onChange={event => setCompare(event.target.checked)}
              inputProps={{ 'aria-label': 'השוואה לשנה קודמת' }}
            />
            <Typography
              sx={{
                color: hasPreviousYear ? textPrimary : axisFg,
                fontSize: 13,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              השוואה לשנה קודמת
            </Typography>
          </Box>

          <Box dir="ltr" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {hasPreviousYear && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <Typography sx={{ color: textPrimary, fontSize: 13 }}>{prevName}</Typography>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: secondColor }} />
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Typography sx={{ color: textPrimary, fontSize: 13 }}>{currName}</Typography>
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: blue }} />
            </Box>
          </Box>
        </Box>
      )}

      {showBarHeader && (
        <Box
          dir="rtl"
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            minHeight: 44,
            pb: 1.25,
            mb: 1.25,
            borderBottom: `1px solid ${grid}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <HeaderTheChart onChange={setModeId} isDarkMode={isDarkMode} />
          </Box>

          <Typography
            sx={{
              color: textPrimary,
              fontSize: { xs: 13, md: 15 },
              fontWeight: 600,
              fontFamily: 'Rubik, sans-serif',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          >
            {headerTitle}
          </Typography>

          <Box
            dir="rtl"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 2,
              color: textPrimary,
              fontFamily: 'Rubik, sans-serif',
              fontSize: { xs: 12, md: 14 },
            }}
          >
            {activeFooterItems.map(item => (
              <Box key={`${item.label}-${item.color}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography component="span">{item.shortLabel ?? item.label}</Typography>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Title block with Divider line matching Figma */}
      {legend2 && !showBarHeader && (
        <Box
          dir={rtl ? 'rtl' : 'ltr'}
          sx={{
            mb: 2,
            width: '100%',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: textPrimary,
              fontSize: '18px',
              fontFamily: 'Rubik, sans-serif',
              textAlign: 'right',
              pb: 1.5,
            }}
          >
            {headerTitle}
          </Typography>
          <Box
            sx={{
              height: '1px',
              width: '100%',
              bgcolor: isDarkMode ? '#374151' : '#E6ECF2',
              mb: 2,
            }}
          />
        </Box>
      )}

      {/* Day / Night / Both Toggle Icons block */}
      {legend2 && !showBarHeader && (
        <Box
          dir={rtl ? 'rtl' : 'ltr'}
          sx={{
            display: 'flex',
            justifyContent: rtl ? 'flex-end' : 'flex-start',
            mb: 2.5,
            width: '100%',
          }}
        >
          <HeaderTheChart onChange={setModeId} isDarkMode={isDarkMode} />
        </Box>
      )}

      <Box ref={chartContainerRef} sx={{ width: '100%', height }}>
        <ResponsiveContainer>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="currGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={blue} stopOpacity={0.5} />
                <stop offset="100%" stopColor={blue} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="prevGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={thirdColor} stopOpacity={0.5} />
                <stop offset="100%" stopColor={thirdColor} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="secondGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={secondColor} stopOpacity={0.5} />
                <stop offset="100%" stopColor={secondColor} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="thirdGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={fourthColor} stopOpacity={0.5} />
                <stop offset="100%" stopColor={fourthColor} stopOpacity={0.1} />
              </linearGradient>
            </defs>

            <CartesianGrid stroke={grid} vertical={false} />

            <XAxis
              dataKey="x"
              tick={{ fill: axisFg, fontSize: 12 }}
              axisLine={{ stroke: isDarkMode ? '#4B5563' : '#E6ECF2' }}
              tickLine={{ stroke: isDarkMode ? '#4B5563' : '#E6ECF2' }}
              interval={xAxisInterval}
              minTickGap={12}
              tickFormatter={(value) => {
                if (periodicData) {
                  // `x` is intentionally a unique internal key (period-0,
                  // period-1, ...). The user-facing value is the period label.
                  const point = data.find(item => item.x === value)
                  return point?.label ?? String(value)
                }
                // Canonical path: value is the date string
                if (typeof value === 'string' && isDateString(value)) {
                  const date = new Date(value)
                  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })
                }
                // Legacy path: value is numeric index, use baseDate calculation
                if (typeof value === 'number') {
                  const baseDate = new Date(2025, 8, 20)
                  const date = new Date(baseDate)
                  date.setDate(baseDate.getDate() + Math.floor(value / 20))
                  return date.toLocaleDateString('en-GB').replace(/\./g, '/')
                }
                return String(value)
              }}
            />

            <YAxis
              tick={{ fill: axisFg, fontSize: 12 }}
              axisLine={{ stroke: isDarkMode ? '#4B5563' : '#E6ECF2' }}
              tickLine={{ stroke: isDarkMode ? '#4B5563' : '#E6ECF2' }}
              domain={[0, 'auto']}
              tickMargin={10}
            />

            {compare && hasPreviousYear && (
              <Area
                type="linear"
                dataKey="prev"
                name={prevName}
                stroke={secondColor}
                strokeWidth={2}
                fill={modeId === 3 ? "none" : "url(#secondGrad)"}
                dot={{ r: 4, stroke: secondColor, strokeWidth: 2, fill: cardBg }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            )}

            <Area
              type="linear"
              dataKey="curr"
              name={currName}
              stroke={blue}
              strokeWidth={2}
              fill={modeId === 3 ? "none" : "url(#currGrad)"}
              dot={{ r: 4, stroke: blue, strokeWidth: 2, fill: cardBg }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            {!periodicData && compare && (
              <Area
                type="linear"
                dataKey="second"
                name={secondName}
                stroke={secondColor}
                strokeWidth={2}
                fill={modeId === 3 ? "none" : "url(#secondGrad)"}
                dot={{ r: 4, stroke: secondColor, strokeWidth: 1.5, fill: '#ffffff' }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            )}

            {!periodicData && (
              <Area
                type="linear"
                dataKey="third"
                name={thirdName}
                stroke={fourthColor}
                strokeWidth={2}
                fill={modeId === 3 ? "none" : "url(#thirdGrad)"}
                dot={{ r: 4, stroke: fourthColor, strokeWidth: 1.5, fill: '#ffffff' }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            )}

            <Tooltip
              content={
                <CustomTooltip currName={currName} prevName={prevName} isDarkMode={isDarkMode} />
              }
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      {/* Footer / Legend at the bottom matching Figma */}
      {legend && (
        <Box
          dir={rtl ? 'rtl' : 'ltr'}
          sx={{
            display: 'flex',
            gap: 3,
            justifyContent: 'flex-start',
            mt: 2.5,
            px: 1,
            flexWrap: 'wrap',
          }}
        >
          {activeFooterItems.map((f, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: isDarkMode ? '#9CA3AF' : '#697077', fontSize: 13, fontFamily: 'Rubik', fontWeight: 500 }}>
                {f.label}
              </Typography>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: f.color, flexShrink: 0 }} />
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  )
}
